export function getPlanProducerPercentage(plan) {
  const row = (plan?.table || []).find((entry) => Number(entry.level) === 0);
  const percentage = Number(row?.percentage);
  return Number.isFinite(percentage) ? percentage : null;
}

export function idOf(ref) {
  if (ref == null) return "";
  if (typeof ref === "string") return ref;
  if (ref._id != null) return String(ref._id);
  return String(ref);
}

export function rankOptions(track) {
  return [...(track?.ranks || [])]
    .sort((left, right) => (left.rankNumber || 0) - (right.rankNumber || 0))
    .map((rank) => ({
      value: rank.code,
      label: `${rank.code || ""} ${rank.name || ""}`.trim(),
    }));
}

const OBJECT_ID_RE = /^[a-f0-9]{24}$/i;

export function pointTypeLabel(ref) {
  if (!ref) return "Point balance";
  if (typeof ref === "object") {
    return `${ref.code || ""} ${ref.name || ""}`.trim() || "Point balance";
  }
  return "Point balance";
}

export function resolvePointTypeLabel(ref, catalog = []) {
  const id = idOf(ref);
  const hit = (catalog || []).find((entry) => idOf(entry) === id);
  if (hit?.name) return hit.name;
  if (hit?.code) return hit.code;
  if (ref && typeof ref === "object") {
    return ref.name || ref.code || "Points";
  }
  if (id && OBJECT_ID_RE.test(id)) return "Points";
  return (typeof ref === "string" && ref) || "Points";
}

export function collectPointTypeTabs({ balances = [], catalog = [] } = {}) {
  const tabs = new Map();
  const add = (ref) => {
    const id = idOf(ref);
    if (!id || tabs.has(id)) return;
    tabs.set(id, {
      id,
      label: resolvePointTypeLabel(ref, catalog),
    });
  };
  (catalog || []).forEach(add);
  (balances || []).forEach((row) => add(row?.pointType));
  return [...tabs.values()];
}

export function buildPromotionRankTimeline({
  history = [],
  currentRankCode,
  ranks = [],
} = {}) {
  const nameOf = (code) =>
    (ranks || []).find((rank) => rank.code === code)?.name || "";

  const chronological = [...(history || [])]
    .filter((row) => row && (row.toRankCode || row.fromRankCode))
    .sort((left, right) => new Date(left.at || 0) - new Date(right.at || 0));

  const rows = [];
  const seen = new Set();
  const push = ({ rankCode, at, source, isCurrent }) => {
    if (!rankCode || seen.has(rankCode)) return;
    seen.add(rankCode);
    rows.push({
      rankCode,
      rankName: nameOf(rankCode),
      at: at || null,
      source: source || null,
      isCurrent: !!isCurrent,
    });
  };

  if (chronological[0]?.fromRankCode) {
    push({
      rankCode: chronological[0].fromRankCode,
      at: null,
      source: null,
      isCurrent: false,
    });
  }
  chronological.forEach((event) => {
    push({
      rankCode: event.toRankCode,
      at: event.at || null,
      source: event.source || null,
      isCurrent: false,
    });
  });

  const current = currentRankCode || rows[rows.length - 1]?.rankCode || null;
  if (current && !seen.has(current)) {
    push({ rankCode: current, at: null, source: null, isCurrent: true });
  }

  rows.reverse();
  return rows.map((row, index) => ({
    ...row,
    isCurrent: index === 0 || row.rankCode === current,
  }));
}

export function collectTrackPointTypes(track) {
  const seen = new Map();
  for (const rank of track?.ranks || []) {
    for (const condition of rank.conditions || []) {
      if (condition.type !== "POINT_BALANCE") continue;
      const id = idOf(condition.pointType);
      if (!id || seen.has(id)) continue;
      seen.set(id, condition.pointType);
    }
  }
  return [...seen.values()];
}

export function promotionProgressHeadline(evaluation) {
  if (!evaluation) {
    return {
      currentCode: null,
      nextRank: null,
      qualifiesTo: null,
      isTop: false,
    };
  }
  const currentCode = evaluation?.currentRank?.code || null;
  const qualifiesTo = evaluation?.promoted ? evaluation.toRankCode || null : null;
  const nextRank = evaluation?.nextRank || null;
  const nextCode = nextRank?.code || null;
  const showNext = Boolean(nextCode && nextCode !== qualifiesTo);
  return {
    currentCode,
    nextRank: showNext ? nextRank : null,
    qualifiesTo,
    isTop: !nextRank && !qualifiesTo,
  };
}

export function conditionItemLabel(item) {
  if (item?.type !== "POINT_BALANCE") {
    return `Direct ${item?.minRankCode || ""}+`;
  }
  const fromMeta = `${item.pointTypeCode || ""} ${item.pointTypeName || ""}`.trim();
  if (fromMeta) return fromMeta;
  return pointTypeLabel(item.pointType);
}

const toAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

const formatUsdWhole = (value) =>
  `USD ${toAmount(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

export function conditionGap(item) {
  const have = toAmount(item?.actual);
  const need = toAmount(item?.required);
  const remaining = Math.max(0, need - have);
  const passed = item?.passed != null ? !!item.passed : have >= need && need > 0;
  const percent =
    need <= 0
      ? passed
        ? 100
        : 0
      : Math.min(100, Math.round((have / need) * 100));
  return { have, need, remaining, percent, passed };
}

export function conditionGapCopy(item, catalog = []) {
  const gap = conditionGap(item);
  if (gap.passed) return "Requirement met";
  if (item?.type === "POINT_BALANCE") {
    const label =
      item.pointTypeName ||
      item.pointTypeCode ||
      resolvePointTypeLabel(item.pointType, catalog);
    return `${formatUsdWhole(gap.remaining)} ${label} left`;
  }
  return `${gap.remaining} more ${item?.minRankCode || ""}+ downlines`;
}

export function buildTrackPathway({
  ranks = [],
  currentRankCode,
  qualifiesTo,
} = {}) {
  const ordered = [...(ranks || [])].sort(
    (left, right) => (left.rankNumber || 0) - (right.rankNumber || 0)
  );
  const currentIndex = ordered.findIndex((rank) => rank.code === currentRankCode);
  const qualifiesIndex = qualifiesTo
    ? ordered.findIndex((rank) => rank.code === qualifiesTo)
    : -1;

  const currentNumber = Number(ordered[currentIndex]?.rankNumber);
  const hasCurrentNumber = currentIndex >= 0 && Number.isFinite(currentNumber);

  return ordered.map((rank, index) => {
    const rankNumber = Number(rank.rankNumber);
    let state = "ahead";
    if (rank.code === currentRankCode) state = "current";
    else if (
      hasCurrentNumber &&
      Number.isFinite(rankNumber) &&
      rankNumber < currentNumber
    ) {
      state = "earned";
    } else if (currentIndex >= 0 && index < currentIndex) state = "earned";
    else if (
      currentIndex >= 0 &&
      qualifiesIndex > currentIndex &&
      index > currentIndex &&
      index <= qualifiesIndex
    ) {
      state = "ready";
    }
    return {
      code: rank.code,
      name: rank.name || "",
      cname: rank.cname || "",
      state,
    };
  });
}

export function promotionCoachLine({ headline, trackName } = {}) {
  if (headline?.qualifiesTo) {
    return `Ready to promote to ${headline.qualifiesTo}`;
  }
  if (headline?.isTop) {
    return `Top of ${trackName || "the track"}`;
  }
  const next = headline?.nextRank;
  if (next?.code) {
    return `Path to ${`${next.code} ${next.name || ""}`.trim()}`;
  }
  return trackName || "";
}
